import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toastHandler } from '@/components/utils/Toast'
import { useAuthContext } from '@/context'
import { useCreateDocument, useUpdateDocument } from '@/services/folders/mutations'
import { keyDocument, keyDocuments } from '@/services/folders/queries'
import { DocumentInterface } from '@/services/folders/types'
import { FileText, Folder, FolderPlus, Loader2 } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { StringParam, useQueryParam } from 'use-query-params'

type FormValues = {
    name: string
    description: string
}

type Props = {
    trigger: ReactNode
    folder?: DocumentInterface | undefined
    parentFolder?: DocumentInterface
    onClose?: () => void
    refetchKey?: string[]
    inDropdown?: boolean
    /** Fallback tag used when parentFolder has no tag. Defaults to 'misc'. */
    defaultTag?: string
}

const ManageMediaFolders = ({ trigger, folder, parentFolder, refetchKey = [], onClose, inDropdown, defaultTag }: Props) => {
    const { orgAccount } = useAuthContext()
    const params = useParams()
    const [open, setOpen] = useState(false)
    const [_zone] = useQueryParam('zone', StringParam)
    const methods = useForm<FormValues>({
        defaultValues: {
            name: folder?.name,
            description: folder?.description,
        },
    })

    const {
        control,
        formState: { isValid },
        handleSubmit,
        reset,
    } = methods

    const { mutate: createFolder, isLoading: creating } = useCreateDocument({
        refetch: [keyDocument(`id=${parentFolder?.id}`), keyDocuments(``), refetchKey],
        onSuccess: () => {
            toastHandler({ message: 'Folder created' })
            reset()
            setOpen(false)
            onClose?.()
        },
    })

    const { mutate: updateDocument, isLoading: updating } = useUpdateDocument({
        refetch: [
            keyDocument(`id=${folder?.id}`),
            keyDocument(`id=${parentFolder?.id}`),
            keyDocument(`id=${params.folder}`),
            keyDocuments(``),
            refetchKey,
        ],
        onSuccess: () => {
            toastHandler({ message: `${folder?.isFolder ? 'Folder' : 'File'} updated` })
            reset()
            setOpen(false)
            onClose?.()
        },
    })

    const onSubmit = (values: any) => {
        const resolvedTag = parentFolder?.tag ?? defaultTag ?? 'misc'
        const payload = {
            ...values,
            project: params.project_id,
            org: orgAccount?.org.id,
            ...(parentFolder ? { parent: parentFolder.id } : {}),
            tag: resolvedTag,
            isFolder: true,
            // Library folders must be browseable via the folder pipeline and
            // visible in media_only searches (both gate on this flag).
            ...(resolvedTag === 'library'
                ? { shared_with_workspace: { enabled: true, role: 'viewer' } }
                : {}),
        }

        if (folder) {
            if (!folder.isFolder) {
                updateDocument({ id: folder.id, data: { name: values.name } })
                return
            }
            const updatePayload = {
                data: payload,
                id: folder.id,
            }
            updateDocument(updatePayload)
            return
        }

        createFolder(payload)
    }

    const loading = creating || updating

    useEffect(() => {
        if (!folder) return
        reset({
            name: folder?.name,
            description: folder?.description,
        })
    }, [folder, reset])

    useEffect(() => {
        reset()
    }, [open, reset])

    return (
        <Dialog
            open={open}
            onOpenChange={value => {
                setOpen(value)
                if (!value) {
                    onClose?.()
                }
            }}
        >
            <DialogTrigger asChild className={inDropdown ? 'w-full' : ''} onClick={e => e.stopPropagation()}>
                {trigger}
            </DialogTrigger>
            <DialogContent className='max-w-md'>
                <DialogHeader>
                    <DialogTitle className='flex items-center gap-2 text-xl'>
                        {folder ? (
                            folder.isFolder ? (
                                <Folder className='w-5 h-5 text-blue-500' />
                            ) : (
                                <FileText className='w-5 h-5 text-gray-500' />
                            )
                        ) : (
                            <FolderPlus className='w-5 h-5 text-blue-500' />
                        )}
                        {folder ? 'Update' : 'Create New'} Media {folder?.isFolder === false ? 'File' : 'Folder'}
                    </DialogTitle>
                    <p className='text-sm text-gray-500 mt-1'>
                        {folder
                            ? `Update the ${folder.isFolder === false ? 'file' : 'folder'} details below`
                            : 'Create a new folder to organize your media files'}
                    </p>
                </DialogHeader>

                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
                        <div className='space-y-4'>
                            <div className='space-y-2'>
                                <Label htmlFor='name' className='text-sm font-medium'>
                                    {folder?.isFolder === false ? 'File' : 'Folder'} name *
                                </Label>
                                <Controller
                                    shouldUnregister
                                    name='name'
                                    rules={{
                                        required: 'Name is required',
                                        minLength: {
                                            value: 1,
                                            message: 'Name must be at least 1 character',
                                        },
                                        maxLength: {
                                            value: 100,
                                            message: 'Name cannot exceed 100 characters',
                                        },
                                    }}
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <div>
                                            <Input
                                                {...field}
                                                placeholder={`Enter ${folder?.isFolder === false ? 'file' : 'folder'} name`}
                                                className={error ? 'border-red-500 focus:border-red-500' : ''}
                                            />
                                            {error && <p className='text-sm text-red-500 mt-1'>{error.message}</p>}
                                        </div>
                                    )}
                                />
                            </div>

                            {(!folder || folder.isFolder !== false) && (
                                <div className='space-y-2'>
                                    <Label htmlFor='description' className='text-sm font-medium'>
                                        Description <span className='text-gray-400'>(optional)</span>
                                    </Label>
                                    <Controller
                                        name='description'
                                        control={control}
                                        rules={{
                                            maxLength: {
                                                value: 500,
                                                message: 'Description cannot exceed 500 characters',
                                            },
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <div>
                                                <Textarea
                                                    {...field}
                                                    id='description'
                                                    placeholder='Add a description to help organize your content'
                                                    rows={3}
                                                    className={`resize-none ${error ? 'border-red-500 focus:border-red-500' : ''}`}
                                                />
                                                {error && <p className='text-sm text-red-500 mt-1'>{error.message}</p>}
                                                <p className='text-xs text-gray-400 mt-1'>
                                                    {field.value?.length || 0}/500 characters
                                                </p>
                                            </div>
                                        )}
                                    />
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <div className='flex gap-3 w-full'>
                                <Button
                                    variant='outline'
                                    type='button'
                                    onClick={() => setOpen(false)}
                                    disabled={loading}
                                    className='flex-1'
                                >
                                    Cancel
                                </Button>
                                <Button disabled={!isValid || loading} type='submit' className='flex-1'>
                                    {loading ? (
                                        <>
                                            <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                                            {folder ? 'Updating...' : 'Creating...'}
                                        </>
                                    ) : (
                                        <>
                                            {folder ? (
                                                folder.isFolder === false ? (
                                                    <FileText className='w-4 h-4 mr-2' />
                                                ) : (
                                                    <Folder className='w-4 h-4 mr-2' />
                                                )
                                            ) : (
                                                <FolderPlus className='w-4 h-4 mr-2' />
                                            )}
                                            {folder ? 'Update' : 'Create'}{' '}
                                            {folder?.isFolder === false ? 'File' : 'Folder'}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}

export default ManageMediaFolders
